import {StopOutlined} from '@ant-design/icons';
import {Button} from 'antd';
import cn from 'classnames';
import React, {useState, FC, useRef} from 'react';

import {ModalOpenerComponent} from 'components/modal-opener-component';
import {resetLoadedData} from 'modules/data/data-actions';
import {selectFormEntityData} from 'modules/data/data-selectors';
import {ETablePageQueryParams} from 'pages/table-page/query-params-aware-table/constants/table-query-params.constants';
import {showMessageFromResponse} from 'shared/utils';
import {useQueryParams} from 'shared/utils/query-params';
import {useAppDispatch, useAppSelector} from 'store/config/hooks';

import {ADMINISTRATION_ACCOUNTS_ENTITY_NAME, BLOCK_BUTTON_TEXTS} from '../../model/constants';
import {IBlockReasonsFormRef, IReasonsRadioButtonData, IBlockUserButtonProps} from '../../model/types';
import {BlockReasonService} from '../../service/block-user.service';
import {BlockReasonsForm} from '../block-reasons-form/block-reasons-form';

import './block-user-button.less';

export const BlockUserButton: FC<IBlockUserButtonProps> = ({referenceUrl, onClose}: IBlockUserButtonProps) => {
    const dispatch = useAppDispatch();
    const {getQueryParams} = useQueryParams();
    const {entity: id} = getQueryParams(Object.values(ETablePageQueryParams));

    const {enabled: isEnabled} = useAppSelector(selectFormEntityData(ADMINISTRATION_ACCOUNTS_ENTITY_NAME))?.data;

    const [isOpen, setOpen] = useState<boolean>(false);
    const [reasonsRadioButtonData, setReasonsRadioButtonData] = useState<Array<IReasonsRadioButtonData>>([]);
    const [isBlockReasons, setIsBlockReasons] = useState<boolean>(false);
    const formRef = useRef<IBlockReasonsFormRef>(null);

    const title = `${isEnabled ? 'Заблокировать' : 'Разблокировать'} пользователя`;

    const handleSave = async () => {
        try {
            const url = `${referenceUrl}/${id}`;
            const formValues = await formRef?.current?.getValues();

            const {response, isError} = await BlockReasonService.changeUserStatus({
                url,
                reasonId: formValues?.reasonId,
                reasonComment: formValues?.comment,
                autoUnlocking: formValues?.autoUnlocking,
            });

            showMessageFromResponse({response, isError});
            dispatch(resetLoadedData(ADMINISTRATION_ACCOUNTS_ENTITY_NAME));
            onClose?.();
        } catch (error) {
            console.error('Ошибка при смене статуса активности пользователя: ', error);
        }
    };

    const handleClick = async () => {
        try {
            const {data} = await BlockReasonService.getBlockListReasons();
            const isReasons = Boolean(isEnabled) && Array.isArray(data) && data?.length !== 0;

            setIsBlockReasons(isReasons);
            setReasonsRadioButtonData(data);
            setOpen(true);
        } catch (error) {
            // TODO для работы с беком 3.1
            if (error.response) {
                setIsBlockReasons(false);
                setOpen(true);
            }
        }
    };

    const handleCancel = () => {
        formRef.current?.reset();
        setOpen(false);
    };

    return (
        <>
            <ModalOpenerComponent
                modalProps={{title: 'Подтвердите действие', width: '580px', centered: true}}
                controlLabels={{save: 'Да', cancel: 'Отменить'}}
                isOpen={isOpen}
                handleCancel={handleCancel}
                handleSave={handleSave}
            >
                {!isBlockReasons ? (
                    <div>{BLOCK_BUTTON_TEXTS.CONFIRM}</div>
                ) : (
                    <BlockReasonsForm
                        ref={formRef}
                        reasonsRadioButtonData={reasonsRadioButtonData}
                    />
                )}
            </ModalOpenerComponent>
            <Button
                className={cn('block-user-button', {danger: isEnabled})}
                type="default"
                icon={isEnabled ? <StopOutlined /> : undefined}
                onClick={handleClick}
            >
                {title}
            </Button>
        </>
    );
};

BlockReasonsForm.displayName = 'BlockReasonsForm';
